import React, { Component, type CanvasHTMLAttributes } from "react";
import SignaturePad, { type FromDataOptions, type PointGroup } from "signature_pad";

import trimCanvas from "./trim";

export interface SignatureCanvasProps extends SignaturePad.Options {
	canvasProps?: CanvasHTMLAttributes<HTMLCanvasElement>;
	clearOnResize?: boolean;
	onBegin?: (event: SignaturePad.SignatureEvent) => void;
	onEnd?: (event: SignaturePad.SignatureEvent) => void;
	backgroundColor?: string;
}

export default class SignatureCanvas extends Component<SignatureCanvasProps> {
	static defaultProps = {
		clearOnResize: true,
	};

	_sigPad: SignaturePad | null = null;
	_canvas: HTMLCanvasElement | null = null;

	_excludeOurProps = () => {
		const { canvasProps, clearOnResize, ...sigPadProps } = this.props;
		return sigPadProps;
	};

	componentDidMount() {
		if (this._canvas) {
			this._sigPad = new SignaturePad(this._canvas, this._excludeOurProps());
			this._resizeCanvas();
			this.on();
		}
	}

	componentWillUnmount() {
		this.off();
	}

	// propagate prop updates to SignaturePad
	componentDidUpdate() {
		if (this._sigPad) {
			Object.assign(this._sigPad, this._excludeOurProps());
		}
	}

	// return the canvas ref for operations like toDataURL
	getCanvas = () => {
		return this._canvas;
	};

	// return a trimmed copy of the canvas
	getTrimmedCanvas = () => {
		// copy the canvas
		const copy = document.createElement("canvas");
		if (this._canvas) {
			copy.width = this._canvas.width;
			copy.height = this._canvas.height;
			const copyCtx = copy.getContext("2d");
			if (copyCtx) {
				if (this.props.backgroundColor) {
					copyCtx.fillStyle = this.props.backgroundColor;
					copyCtx.fillRect(0, 0, copy.width, copy.height);
				}
				copyCtx.drawImage(this._canvas, 0, 0);
			}
		}
		// then trim it
		return trimCanvas(copy);
	};

	// return the internal SignaturePad reference
	getSignaturePad = () => {
		return this._sigPad;
	};

	_checkClearOnResize = () => {
		if (!this.props.clearOnResize) {
			return;
		}
		this._resizeCanvas();
	};

	_resizeCanvas = () => {
		const canvasProps = this.props.canvasProps || {};
		const { width, height } = canvasProps;
		// don't resize if the canvas has fixed width and height
		if (width && height) {
			return;
		}

		const canvas = this._canvas;
		if (!canvas) return;

		/* When zoomed out to less than 100%, for some very strange reason,
		  some browsers report devicePixelRatio as less than 1
		  and only part of the canvas is cleared then. */
		const ratio = Math.max(window.devicePixelRatio || 1, 1);

		if (!width) {
			canvas.width = canvas.offsetWidth * ratio;
		}
		if (!height) {
			canvas.height = canvas.offsetHeight * ratio;
		}
		canvas.getContext("2d")?.scale(ratio, ratio);
		this.clear();
	};

	render() {
		const { canvasProps } = this.props;
		return (
			<canvas
				ref={(ref) => {
					this._canvas = ref;
				}}
				{...canvasProps}
			/>
		);
	}

	// all wrapper functions below render
	//
	on = () => {
		window.addEventListener("resize", this._checkClearOnResize);
		return this._sigPad?.on();
	};

	off = () => {
		window.removeEventListener("resize", this._checkClearOnResize);
		return this._sigPad?.off();
	};

	clear = () => {
		return this._sigPad?.clear();
	};

	isEmpty = () => {
		return this._sigPad?.isEmpty();
	};

	fromDataURL = (dataURL: string, options?: SignaturePad.FromDataUrlOptions) => {
		return this._sigPad?.fromDataURL(dataURL, options);
	};

	toDataURL = (type?: string, encoderOptions?: number | SignaturePad.ToSVGOptions) => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return this._sigPad?.toDataURL(type, encoderOptions);
	};

	fromData = (pointGroups: PointGroup[], options?: FromDataOptions) => {
		return this._sigPad?.fromData(pointGroups, options);
	};

	toData = () => {
		return this._sigPad?.toData();
	};
}
